import { useMemo } from 'react';
import styled from '@emotion/styled';
import { tokens, IconButton, Text } from '@hotjar/hotsauce';
import { isEmpty } from 'lodash-es';
import { useHelpHotjarUrl } from '@hotjar/translations';
import type { ClickedSubFilter } from '../ClickedFilterType';
import { SubFilterClickedKind } from '../ClickedFilterType';
import {
  COLUMN_ONE_WIDTH,
  COLUMN_TWO_WIDTH,
  SUB_FILTER_KIND_OPTION_LIST,
  PLACEHOLDER_BY_TYPE,
} from './ClickedFilterConstant';
import {
  FUNNEL_STEP_TEXT_OPERATION_OPTION_LIST,
  TEXT_OPERATION_OPTION_LIST,
  URL_OPERATION_OPTION_LIST,
} from '../../GenericConstants/GenericFilterOperation';
import { CornerLine } from '../../GenericBloc/GenericSubFilter/CornerLine';
import { GenericFilterSelect } from '../../GenericBloc/GenericFilterSelect/GenericFilterSelect';
import { RadioTextFilterSection } from './RadioTextFilterSection';

const DeleteIcon = styled(IconButton)`
  top: ${tokens.spacer.x1};
  right: ${tokens.spacer.x1};
`;

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tokens.spacer.x0};
`;

const Wrapper = styled.div`
  display: flex;
  gap: ${tokens.spacer.x1};
`;

const KindSelectedWrapper = styled.div`
  flex-grow: 1;
`;

const FirstColumnWrapper = styled.div`
  display: flex;
  min-width: ${COLUMN_ONE_WIDTH}px;
  align-items: center;
  gap: ${tokens.spacer.x1};
`;

const SecondAndThirdColumn = styled.div`
  display: flex;
  align-items: center;
  flex: auto;
  gap: ${tokens.spacer.x1};
`;

const FirstSecondColumnFiller = styled.div`
  min-width: ${COLUMN_ONE_WIDTH + COLUMN_TWO_WIDTH + 2 * 4}px;
`;

interface SubFilterWrapperProps {
  value: ClickedSubFilter;
  isFirstSubFilter?: boolean;
  onChange: (newValue: ClickedSubFilter) => void;
  onDelete: () => void;
  showValidationError: boolean;
  isFunnelStep?: boolean;
}

export const SubFilterWrapper = ({
  value,
  isFirstSubFilter = false,
  onChange,
  onDelete,
  showValidationError,
  isFunnelStep,
}: SubFilterWrapperProps): JSX.Element => {
  const helpHotjarUrl = useHelpHotjarUrl();
  const getOptionList = useMemo(() => {
    if (value.kind === SubFilterClickedKind.PAGE_PATH) {
      return URL_OPERATION_OPTION_LIST;
    }
    if (isFunnelStep === true) {
      return FUNNEL_STEP_TEXT_OPERATION_OPTION_LIST;
    }
    return TEXT_OPERATION_OPTION_LIST;
  }, [value.kind, isFunnelStep]);

  return (
    <ErrorWrapper>
      <Wrapper>
        <FirstColumnWrapper>
          <CornerLine />
          <div>{isFirstSubFilter ? 'where' : 'and where'}</div>
          <KindSelectedWrapper>
            <GenericFilterSelect
              value={value.kind}
              optionList={SUB_FILTER_KIND_OPTION_LIST}
              onChange={(newKindValue) => {
                const newValue = {
                  ...value,
                  kind: newKindValue,
                  value: {
                    // We also reset the operation and the input string because they depends on the kind
                    operation:
                      newKindValue === SubFilterClickedKind.PAGE_PATH
                        ? URL_OPERATION_OPTION_LIST[0].value
                        : TEXT_OPERATION_OPTION_LIST[0].value,
                    value: '',
                  },
                };
                onChange(newValue);
              }}
              size="s"
            />
          </KindSelectedWrapper>
        </FirstColumnWrapper>
        <SecondAndThirdColumn>
          <RadioTextFilterSection
            value={value.value}
            onChange={(newRadioTextValue) => {
              const newValue = {
                ...value,
                value: newRadioTextValue,
              };
              onChange(newValue);
            }}
            optionList={getOptionList}
            placeholders={PLACEHOLDER_BY_TYPE[value.kind]}
            showValidationError={showValidationError}
            infoFooter={
              value.kind === SubFilterClickedKind.PAGE_PATH
                ? {
                    link: `${helpHotjarUrl}/articles/8983796165911`,
                    label: 'More about filtering options',
                  }
                : undefined
            }
            removeSpaceInInput={value.kind === SubFilterClickedKind.SELECTOR}
          />
          <DeleteIcon
            tooltipText="Clear"
            description="Clear"
            kind="ghost"
            icon="close"
            size="s"
            onClick={onDelete}
          />
        </SecondAndThirdColumn>
      </Wrapper>
      {showValidationError &&
        value.value &&
        'value' in value.value &&
        isEmpty(value.value.value) && (
          <Wrapper>
            <FirstSecondColumnFiller />
            <Text textColor="text_error">Enter text</Text>
          </Wrapper>
        )}
    </ErrorWrapper>
  );
};
